import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { TextField } from "@material-ui/core";
import { Button } from "../../components";
import { DOCUMENT_ACTION } from "../../redux/action";
import { Toaster } from "../../utils/common";
import { canManageDocumentTemplates } from "../../utils/documentTemplateAccess";
import {
  DOCUMENT_UPLOAD_ACCEPT,
  DOCUMENT_UPLOAD_SIZE_NOTE,
  filterValidDocumentUploadFiles,
} from "../../utils/documentUploadValidation";
import {
  openDocumentInNewTab,
  openDocumentPreviewTab,
} from "../../utils/openDocumentInNewTab";
import "./templates.scss";

const TABS = [
  { id: "individual", label: "Individual" },
  { id: "business", label: "Business" },
];

export default function Templates() {
  const [category, setCategory] = useState("individual");
  const [templateName, setTemplateName] = useState("");
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);

  const loadTemplates = useCallback(async (nextCategory) => {
    setLoading(true);
    try {
      const res = await DOCUMENT_ACTION.listDocumentTemplates(nextCategory);
      setRows(res?.status === 1 && Array.isArray(res.data) ? res.data : []);
      if (res && res.status !== 1) {
        Toaster.error(res.message || "Could not load templates");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates(category);
  }, [category, loadTemplates]);

  if (!canManageDocumentTemplates()) {
    return <Navigate to="/forbidden" replace />;
  }

  const saveTemplate = async () => {
    const name = templateName.trim();
    if (!name) {
      Toaster.warning("Please enter a template name");
      return;
    }
    if (!file) {
      Toaster.warning("Please upload a file");
      return;
    }
    const { validFiles, errors } = filterValidDocumentUploadFiles([file]);
    if (errors.length || !validFiles.length) {
      Toaster.error(errors[0] || "Please choose a valid file");
      return;
    }

    setSaving(true);
    try {
      const res = await DOCUMENT_ACTION.createDocumentTemplate({
        template_name: name,
        category,
        file: validFiles[0],
      });
      if (res?.status === 1) {
        Toaster.success(res.message || "Template saved");
        setTemplateName("");
        setFile(null);
        setFileInputKey((value) => value + 1);
        await loadTemplates(category);
      } else {
        Toaster.error(res?.message || "Could not save template");
      }
    } finally {
      setSaving(false);
    }
  };

  const previewTemplate = async (row) => {
    const previewTab = openDocumentPreviewTab();
    try {
      const result = await DOCUMENT_ACTION.downloadDocumentTemplate({
        template_id: row.template_id,
        name: row.file_name,
        contentType: row.content_type,
        preview: true,
      });
      await openDocumentInNewTab(
        result?.blobUrl,
        result?.documentType || row.content_type,
        result?.documentName || row.file_name,
        previewTab,
      );
    } catch (error) {
      if (previewTab && !previewTab.closed) previewTab.close();
      Toaster.error(error?.message || "Could not preview template");
    }
  };

  const downloadTemplate = async (row) => {
    try {
      await DOCUMENT_ACTION.downloadDocumentTemplate({
        template_id: row.template_id,
        name: row.file_name,
        contentType: row.content_type,
      });
    } catch (error) {
      Toaster.error(error?.message || "Could not download template");
    }
  };

  const deleteTemplate = async (row) => {
    const res = await DOCUMENT_ACTION.deleteDocumentTemplate(row.template_id);
    if (res?.status === 1) {
      Toaster.success(res.message || "Template deleted");
      await loadTemplates(category);
    } else {
      Toaster.error(res?.message || "Could not delete template");
    }
  };

  return (
    <div className="templates-page">
      <div className="templates-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={
              category === tab.id
                ? "templates-tab is-active"
                : "templates-tab"
            }
            onClick={() => setCategory(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <section className="templates-panel">
        <div className="templates-panel-head">
          <h2>Add template</h2>
          <p>
            Name the template and upload the file for{" "}
            {category === "business" ? "business" : "individual"} requests.
          </p>
        </div>
        <div className="templates-form">
          <TextField
            className="templates-name"
            label="Template name"
            variant="outlined"
            fullWidth
            value={templateName}
            inputProps={{ maxLength: 255 }}
            onChange={(event) => setTemplateName(event.target.value)}
          />
          <div className="templates-file">
            <label className="templates-file-box">
              <span className="templates-file-label">File</span>
              <span
                className={
                  file
                    ? "templates-file-name"
                    : "templates-file-name is-empty"
                }
              >
                {file ? file.name : "Choose a file"}
              </span>
              <span className="templates-file-action">Browse</span>
              <input
                key={fileInputKey}
                type="file"
                accept={DOCUMENT_UPLOAD_ACCEPT}
                onChange={(event) => {
                  setFile(event.target.files?.[0] || null);
                }}
              />
            </label>
          </div>
          <div className="templates-save">
            <Button
              text={saving ? "Saving…" : "Save template"}
              disable={saving}
              handleClick={saveTemplate}
            />
          </div>
        </div>
        <span className="templates-note">{DOCUMENT_UPLOAD_SIZE_NOTE}</span>
      </section>

      <section className="templates-panel">
        <h2>{category === "business" ? "Business" : "Individual"} templates</h2>
        {loading ? (
          <p className="templates-empty">Loading templates…</p>
        ) : rows.length === 0 ? (
          <p className="templates-empty">No templates saved yet.</p>
        ) : (
          <table className="templates-table">
            <thead>
              <tr>
                <th>Template name</th>
                <th>File</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.template_id}>
                  <td>{row.template_name}</td>
                  <td>{row.file_name}</td>
                  <td className="templates-actions">
                    <button type="button" onClick={() => previewTemplate(row)}>
                      Preview
                    </button>
                    <button type="button" onClick={() => downloadTemplate(row)}>
                      Download
                    </button>
                    <button
                      type="button"
                      className="templates-delete"
                      onClick={() => deleteTemplate(row)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
